import Axios from "axios";
import { stringify } from "qs";
import { getStorage } from "@/utils/storage";
import store from "@/store";
import { Toast, Dialog } from "vant";
import md5 from "js-md5";

/**获取sign序列 */
export const getSign = (obj) => {
  const str = stringify(obj);
  return md5(str).toUpperCase();
};

const baseHeaders = () => {
  return {
    api_client_code: process.env.VUE_APP_SYSTEM_CODE,
    api_version: process.env.VUE_APP_API_VERSION,
    api_timestamp: (+new Date()).toString(),
  };
};

const headerConfig = (config, token) => {
  const base = baseHeaders();
  let headers = config.headers;
  headers = { ...headers, ...base };
  headers["api_token"] = token;
  headers["api_sign"] = getSign({
    api_token: token,
    ...base,
  });
  return headers;
};

// 相关配置请参考：www.axios-js.com/zh-cn/docs/#axios-request-config-1
const defaultConfig = {
  // 请求超时时间
  timeout: 10000 * 6,
  baseURL: process.env.VUE_APP_GATEWAY_URL,
  headers: baseHeaders(),
};

class Http {
  constructor() {
    this.httpInterceptorsRequest();
    this.httpInterceptorsResponse();
  }

  /** token过期后，暂存待执行的请求 */
  static requests = [];

  /** 提示执行次数，用于防止消息多次提醒 */
  static comfirmTimes = 0;

  /** 防止重复刷新token */
  static isRefreshing = false;

  /** 初始化配置对象 */
  static initConfig = {};

  /** 保存当前Axios实例对象 */
  static axiosInstance = Axios.create(defaultConfig);

  /** 重连原始请求 */
  static retryOriginalRequest(config) {
    return new Promise((resolve) => {
      Http.requests.push((token) => {
        config.headers = headerConfig(config, token);
        resolve(config);
      });
    });
  }

  /** 请求拦截 */
  httpInterceptorsRequest() {
    Http.axiosInstance.interceptors.request.use(
      async (config) => {
        // 优先判断post/get等方法是否传入回掉，否则执行初始化设置等回掉
        if (typeof config.beforeRequestCallback === "function") {
          config.beforeRequestCallback(config);
          return config;
        }
        if (Http.initConfig.beforeRequestCallback) {
          Http.initConfig.beforeRequestCallback(config);
          return config;
        }
        /** 请求白名单，放置一些不需要token的接口（通过设置请求白名单，防止token过期后再请求造成的死循环问题） */
        const whiteList = [
          "/user/accountlogin/refreshtoken",
          "/user/accountlogin/tempcode",
          "/user/accountlogin/authorizationLogin",
        ];
        return whiteList.some((v) => config.url?.indexOf(v) > -1)
          ? config
          : new Promise((resolve) => {
              const data = getStorage("tokenInfo");
              if (data) {
                const now = new Date().getTime();
                const tokenDeadline = new Date(data.tokenDeadline).getTime();
                const expired = tokenDeadline - now <= 0;
                if (expired) {
                  if (!Http.isRefreshing) {
                    Http.isRefreshing = true;
                    // token过期刷新
                    store
                      .dispatch("app/getToken", data.refreshToken)
                      .then((res) => {
                        const token = res.token;
                        config.headers = headerConfig(config, token);
                        Http.requests.forEach((cb) => cb(token));
                        Http.requests = [];
                      })
                      .catch(() => {
                        Dialog({ message: "尝试刷新token失败，请刷新页面" });
                        localStorage.clear();
                      })
                      .finally(() => {
                        Http.isRefreshing = false;
                      });
                  }
                  resolve(Http.retryOriginalRequest(config));
                } else {
                  config.headers = headerConfig(config, data.token);
                  resolve(config);
                }
              } else {
                resolve(config);
              }
            });
      },
      (error) => {
        return Promise.reject(error);
      }
    );
  }

  /** 响应拦截 */
  httpInterceptorsResponse() {
    const instance = Http.axiosInstance;
    instance.interceptors.response.use(
      (response) => {
        // 关闭进度条动画
        Toast.clear();
        const $config = response.config;
        //排除一些不返回code的接口
        const code = response.data.code ?? "no";
        if (code === 101 || code == 102) {
          // //退出登录
          localStorage.clear();
          store.dispatch("app/getToken");
          return new Promise(() => {});
        }
        if (code != 0 && code !== "no") {
          Dialog({ message: response.data.msg || "请求错误" });
          return Promise.reject(response.data);
        }
        // 优先判断post/get等方法是否传入回掉，否则执行初始化设置等回掉
        if (typeof $config.beforeResponseCallback === "function") {
          $config.beforeResponseCallback(response);
          return response.data;
        }
        if (Http.initConfig.beforeResponseCallback) {
          Http.initConfig.beforeResponseCallback(response);
          return response.data;
        }
        return response.data;
      },
      (error) => {
        const $error = error;
        $error.isCancelRequest = Axios.isCancel($error);
        // 关闭进度条动画
        Toast.clear();
        localStorage.clear();
        // 所有的响应异常 区分来源为取消请求/非取消请求
        return Promise.reject($error);
      }
    );
  }

  /** 通用请求工具函数 */
  request = (options) => {
    // 开启进度条动画
    if (options?.withLoading !== false) {
      Toast.loading({
        message: "加载中...",
        forbidClick: true,
      });
    }
    return Http.axiosInstance(options);
  };

  /** 单独抽离的post工具函数 */
  post(url, data, config) {
    return this.request({ method: "post", url, data, ...config });
  }

  /** 单独抽离的get工具函数 */
  get(url, params, config) {
    return this.request({ method: "get", url, params, ...config });
  }
}

export const http = new Http();
export default http;
